import { http } from "../utils/http";
import { API_ENDPOINTS, PAGE_SIZE } from "../config";
import { store } from "../State/store/configureStore";
import { isStaff, ROLE_FACILITY_ADMIN } from "../security/roles";
import { closeProgress, showProgress } from "../State/actions/uploadProgress";
import globalStaffCache from "./globalStaffCache";
import globalCacheService from "./globalCacheService";

// Remove all caching logic from here as it's now in staffDataLoader.js

class StaffService {
  /**
   * Delete a staff member
   * @param {string} staffId - ID of the staff to delete
   * @returns {Promise<Object>} The API response
   */
  static async delete(staffId) {
    try {
      const response = await http.request(API_ENDPOINTS.deleteStaff(staffId), "delete");
      // Clear global staff cache
      globalStaffCache.data = null;
      globalStaffCache.staffOptions = null;
      return response;
    } catch (error) {
      console.error("Error deleting staff:", error);
      throw error;
    }
  }

  /**
   * Invite a new staff member
   * @param {Object} data - Staff data
   * @param {boolean} activeInternal - Whether to make the user active internally
   * @returns {Promise<Object>} The API response
   */
  static async invite(data, activeInternal) {
    try {
      let url = API_ENDPOINTS.inviteStaff(
        data.hasOwnProperty("loginUserId") || activeInternal
      );
      if (activeInternal) {
        url += "?makeActiveInternal=true";
      }
      const response = await http.request(url, "post", data);
      // Clear global staff cache
      globalStaffCache.data = null;
      globalStaffCache.staffOptions = null;
      return response;
    } catch (error) {
      console.error("Error inviting staff:", error);
      throw error;
    }
  }

  /**
   * Load staff members with caching support
   * @param {number} page - Page number (0-indexed)
   * @param {boolean} all - Whether to load all staff
   * @param {boolean} showInactive - Whether to include inactive staff
   * @param {boolean} forOrg - Whether to load for organization
   * @param {string} searchTerm - Search term for filtering
   * @returns {Promise<Object>} Staff data with pagination info
   */
  static async load(page = 0, all = false, showInactive = false, forOrg = false, searchTerm = '') {
    try {
      // Handle common caching scenarios for performance
      if (!showInactive && !forOrg && !searchTerm && page === 0 && all) {
        if (globalStaffCache.data && globalStaffCache.timestamp > Date.now() - 5 * 60 * 1000) {
          return {
            _embedded: { staffs: globalStaffCache.data },
            page: { totalElements: globalStaffCache.data.length }
          };
        }
      }

      let api = null;
      let role = store.getState().user.role;
      if (isStaff(role)) {
        if (forOrg) {
          api = API_ENDPOINTS.loadStaff();
        } else {
          api = API_ENDPOINTS.loadAllStaffForStaff();
        }
      } else {
        api = API_ENDPOINTS.loadStaff();
      }

      const pageSize = all ? 1000 : 100;

      api += `?page=${page}&size=${pageSize}`;
      
      if (showInactive) api += "&allStatus=true";
      if (forOrg) api += "&forOrg=true";
      if (searchTerm) api += `&searchTerm=${encodeURIComponent(searchTerm)}`;

      const data = await http.request(api);

      if (data && data._embedded) {
        const staffs = data._embedded.staffs || [];
        
        // Cache the data for common use cases
        if (!showInactive && !forOrg && !searchTerm && page === 0 && all) {
          globalStaffCache.data = staffs;
          globalStaffCache.timestamp = Date.now();
        }

        return {
          data: staffs,
          loadMore: data.page.totalPages > page + 1,
          totalStaff: data.page.totalElements,
        };
      } else {
        return {
          data: [],
          loadMore: false,
          totalStaff: 0,
        };
      }
    } catch (error) {
      console.error("Error loading staff:", error);
      return {
        data: [],
        loadMore: false,
        totalStaff: 0,
      };
    }
  }

  static async loadConsultants() {
    const data = await http.request(API_ENDPOINTS.consultants);
    return data && data._embedded ? data._embedded.staffWithRoles : [];
  }

  static loadOneStaff(staffId) {
    let api = API_ENDPOINTS.loadOneStaff(staffId);
    return http.request(api);
  }

  static loadProfile() {
    return http.request(API_ENDPOINTS.loadStaffProfile);
  }

  static editProfile(data) {
    return http.request(API_ENDPOINTS.loadStaffProfile, "patch", data);
  }

  static createCareTeam(data) {
    return http.request(API_ENDPOINTS.careTeams, "post", data);
  }

  static editCareTeam(careTeamId, data) {
    return http.request(
      API_ENDPOINTS.careTeams + "/" + careTeamId,
      "patch",
      data
    );
  }

  static deleteCareTeam(careTeamId) {
    return http.request(API_ENDPOINTS.careTeams + "/" + careTeamId, "delete");
  }

  static async getCareTeams() {
    const data = await http.request(
      API_ENDPOINTS.careTeams + "?page=0&size=500"
    );
    if (data && data._embedded) {
      return data._embedded.careTeams;
    } else {
      return [];
    }
  }

  /**
   * Load staff members
   * @param {number} page - Page number for pagination
   * @param {boolean} all - Whether to load all staff
   * @param {boolean} showInactive - Whether to include inactive staff
   * @param {boolean} forOrg - Whether to load staff for organization
   * @param {string} searchTerm - Search term for filtering staff
   * @returns {Promise<Object>} Staff data with pagination info
   */
  static async load(page, all, showInactive, forOrg, searchTerm) {
    try {
      // For the common case that multiple components need (staff list), use the global cache
      if (page === 0 && all === true && !showInactive && !forOrg && !searchTerm) {
        // Check if we already have staff data in the global cache
        if (globalStaffCache.data) {
          return globalStaffCache.data;
        }
        
        // If we reach here, we need to make the API call and cache the result
        let api = null;
        let role = store.getState().user.role;
        if (isStaff(role)) {
          api = API_ENDPOINTS.loadAllStaffForStaff();
        } else {
          api = API_ENDPOINTS.loadStaff();
        }
        
        api = api + `?page=0&size=1000`;
        
        const data = await http.request(api);
        const result = data && data._embedded 
          ? {
              data: data._embedded.staffs || [],
              loadMore: data.page.totalPages > 0 + 1,
              totalStaff: data.page.totalElements,
            }
          : {
              data: [],
              loadMore: false,
              totalStaff: 0,
            };
            
        // Save to global cache
        globalStaffCache.setData(result);
        return result;
      }
  
      // For other cases (filtering, pagination, etc.), make a direct API request
      let api = null;
      let role = store.getState().user.role;
      if (isStaff(role)) {
        if (forOrg) {
          api = API_ENDPOINTS.loadStaff();
        } else {
          api = API_ENDPOINTS.loadAllStaffForStaff();
        }
      } else {
        api = API_ENDPOINTS.loadStaff();
      }
  
      let pageSize = 10;
      if (all) {
        pageSize = 1000;
      }
  
      api = api + `?page=${page}&size=${pageSize}`;
      if (showInactive) {
        api += "&allStatus=true";
      }
  
      if (forOrg) {
        api += "&forOrg=true";
      }
  
      if (searchTerm) {
        api += "&searchTerm=" + searchTerm;
      }
  
      const data = await http.request(api);
      return data && data._embedded 
        ? {
            data: data._embedded.staffs || [],
            loadMore: data.page.totalPages > page + 1,
            totalStaff: data.page.totalElements,
          }
        : {
            data: [],
            loadMore: false,
            totalStaff: 0,
          };
    } catch (error) {
      console.error("Error loading staff:", error);
      return {
        data: [],
        loadMore: false,
        totalStaff: 0,
      };
    }
  }

  /**
   * Load consultants
   * @returns {Promise<Array>} List of consultants
   */
  static async loadConsultants() {
    try {
      const data = await http.request(API_ENDPOINTS.consultants);
      return data && data._embedded ? data._embedded.staffWithRoles : [];
    } catch (error) {
      console.error("Error loading consultants:", error);
      return [];
    }
  }

  /**
   * Load a single staff member
   * @param {string} staffId - ID of the staff to load
   * @returns {Promise<Object>} Staff data
   */
  static async loadOneStaff(staffId) {
    try {
      const api = API_ENDPOINTS.loadOneStaff(staffId);
      return await http.request(api);
    } catch (error) {
      console.error("Error loading staff member:", error);
      throw error;
    }
  }

  /**
   * Load staff profile
   * @returns {Promise<Object>} Profile data
   */
  static async loadProfile() {
    try {
      return await http.request(API_ENDPOINTS.loadStaffProfile);
    } catch (error) {
      console.error("Error loading staff profile:", error);
      throw error;
    }
  }

  /**
   * Edit staff profile
   * @param {Object} data - Updated profile data
   * @returns {Promise<Object>} Updated profile
   */
  static async editProfile(data) {
    try {
      return await http.request(API_ENDPOINTS.loadStaffProfile, "patch", data);
    } catch (error) {
      console.error("Error updating staff profile:", error);
      throw error;
    }
  }

  /**
   * Create a care team
   * @param {Object} data - Care team data
   * @returns {Promise<Object>} Created care team
   */
  static async createCareTeam(data) {
    try {
      const response = await http.request(API_ENDPOINTS.careTeams, "post", data);
      // Clear care teams cache when a new care team is created
      this.clearCareTeamsCache();
      return response;
    } catch (error) {
      console.error("Error creating care team:", error);
      throw error;
    }
  }
  
  /**
   * Helper method to clear all care teams caches
   */
  static clearCareTeamsCache() {
    globalCacheService.clearCachesByPrefix('care_teams_');
  }

  /**
   * Edit a care team
   * @param {string} careTeamId - ID of the care team to edit
   * @param {Object} data - Updated care team data
   * @returns {Promise<Object>} Updated care team
   */
  static async editCareTeam(careTeamId, data) {
    try {
      const response = await http.request(
        API_ENDPOINTS.careTeams + "/" + careTeamId,
        "patch",
        data
      );
      // Clear care teams cache when a care team is updated
      this.clearCareTeamsCache();
      return response;
    } catch (error) {
      console.error("Error editing care team:", error);
      throw error;
    }
  }

  /**
   * Delete a care team
   * @param {string} careTeamId - ID of the care team to delete
   * @returns {Promise<Object>} API response
   */
  static async deleteCareTeam(careTeamId) {
    try {
      const response = await http.request(API_ENDPOINTS.careTeams + "/" + careTeamId, "delete");
      // Clear care teams cache when a care team is deleted
      this.clearCareTeamsCache();
      return response;
    } catch (error) {
      console.error("Error deleting care team:", error);
      throw error;
    }
  }

  /**
   * Get all care teams with caching
   * @param {string} [forOrg] - Optional organization ID to filter care teams
   * @returns {Promise<Array>} List of care teams
   */
  static async getCareTeams(forOrg) {
    // Create a cache key based on the optional organization filter
    const cacheKey = `care_teams_${forOrg || 'all'}`;
    
    try {
      // Use global cache service to prevent duplicate API calls
      return await globalCacheService.cacheApiCall(
        cacheKey,
        async () => {
          // Build the API URL with proper params
          let api = API_ENDPOINTS.careTeams + "?page=0&size=500";
          if (forOrg) {
            api += "&forOrg=" + forOrg;
          }
          
          // Make the actual API call
          const data = await http.request(api);
          
          // Process and return the result
          if (data && data._embedded) {
            return data._embedded.careTeams || [];
          } else {
            return [];
          }
        },
        15 * 60 * 1000 // 15 minute cache TTL
      );
    } catch (error) {
      console.error("Error getting care teams:", error);
      return [];
    }
  }

  /**
   * Edit a staff member
   * @param {string} staffId - ID of the staff to edit
   * @param {Object} data - Updated staff data
   * @returns {Promise<Object>} Updated staff
   */
  static async editStaff(staffId, data) {
    try {
      const response = await http.request(API_ENDPOINTS.editStaff(staffId), "patch", data);
      // Clear global staff cache
      globalStaffCache.data = null;
      globalStaffCache.staffOptions = null;
      return response;
    } catch (error) {
      console.error("Error editing staff:", error);
      throw error;
    }
  }

  /**
   * Reinvite a staff member
   * @param {string} staffId - ID of the staff to reinvite
   * @param {Object} data - Reinvite data
   * @returns {Promise<Object>} API response
   */
  static async reinviteStaff(staffId, data) {
    try {
      return await http.request(API_ENDPOINTS.reInviteStaff(staffId), "post", data);
    } catch (error) {
      console.error("Error reinviting staff:", error);
      throw error;
    }
  }

  /**
   * Get staff locations
   * @returns {Promise<Array>} List of locations
   */
  static async getStaffLocations() {
    try {
      const data = await http.request(API_ENDPOINTS.allResmedLocations);
      // {"Response":{"totalCount":"1","locations":[{"name":"PEI - MPS","id":"4783"}]}}
      return data && data.Response ? data.Response.locations || [] : [];
    } catch (error) {
      console.error("Error getting staff locations:", error);
      return [];
    }
  }

  /**
   * Get clinical user names by location
   * @param {string} locId - Location ID
   * @returns {Promise<Array>} List of clinical users
   */
  static async getClinicalUSerNames(locId) {
    try {
      const data = await http.request(API_ENDPOINTS.resmedUsers(locId));
      return data && data.Response ? data.Response.clinicalUsers || [] : [];
    } catch (error) {
      console.error("Error getting clinical user names:", error);
      return [];
    }
  }

  /**
   * Restore a staff member
   * @param {string} staffId - ID of the staff to restore
   * @returns {Promise<Object>} API response
   */
  static async restoreStaff(staffId) {
    try {
      return await http.request(API_ENDPOINTS.restoreStaff(staffId), "post");
    } catch (error) {
      console.error("Error restoring staff:", error);
      throw error;
    }
  }

  /**
   * Reset a staff member's password
   * @param {string} staffId - ID of the staff
   * @param {string} loginUserId - Login user ID
   * @returns {Promise<Object>} API response
   */
  static async resetPassword(staffId, loginUserId) {
    try {
      return await http.request(API_ENDPOINTS.resetStaffPassword(staffId), "post", {
        loginUserId,
      });
    } catch (error) {
      console.error("Error resetting password:", error);
      throw error;
    }
  }

  static async importUsingCSVFile(csvFile) {
    const formData = new FormData();
    formData.append("files", csvFile);
    const response = await http.request(
      API_ENDPOINTS.importStaff(),
      "post",
      formData,
      null,
      (progressEvent) => {
        const uploadProgress = Math.round(
          (progressEvent.loaded * 100) / progressEvent.total
        );
        store.dispatch(showProgress(uploadProgress));
      },
      120000
    );
    store.dispatch(closeProgress());
    let strings = [];
    if (response._embedded && response._embedded.strings) {
      strings = response._embedded.strings || [];
    }
    return strings;
  }

  /**
   * Load all organizations with caching
   * @returns {Promise<Object>} Organizations data
   */
  static async loadOrganizations() {
    // Create a cache key for organizations
    const cacheKey = 'organizations_all';
    
    try {
      // Use global cache service to prevent duplicate API calls
      return await globalCacheService.cacheApiCall(
        cacheKey,
        async () => {
          const data = await http.request(API_ENDPOINTS.organizations());
          return data;
        },
        30 * 60 * 1000 // 30 minute cache TTL
      );
    } catch (error) {
      console.error("Error loading organizations:", error);
      throw error;
    }
  }

  /**
   * Load sub-organizations for a specific organization with caching
   * @param {string} orgId - Parent organization ID
   * @returns {Promise<Object>} Sub-organizations data
   */
  static async loadSubOrganizations(orgId) {
    // Create a cache key based on the parent organization ID
    const cacheKey = `sub_organizations_${orgId}`;
    
    try {
      // Use global cache service to prevent duplicate API calls
      return await globalCacheService.cacheApiCall(
        cacheKey,
        async () => {
          const data = await http.request(API_ENDPOINTS.subOrganizations(orgId));
          return data;
        },
        30 * 60 * 1000 // 30 minute cache TTL
      );
    } catch (error) {
      console.error("Error loading sub-organizations:", error);
      throw error;
    }
  }

  /**
   * Load an organization
   * @param {string} organizationId - ID of the organization to load
   * @returns {Promise<Object>} Organization data
   */
  static async loadOrganization(organizationId) {
    try {
      const api = API_ENDPOINTS.organizations(organizationId);
      return await http.request(api);
    } catch (error) {
      console.error("Error loading organization:", error);
      throw error;
    }
  }
  
  static async loadStaffByRoleId(roleId) {
    const filter = `?page=0&size=1000&roleId=${roleId}`;
    let response = await http.request(API_ENDPOINTS.loadAllStaffForStaff() + filter);
    return response._embedded?.staffs || []
  }

  /**
   * Helper method to clear organizations cache
   */
  static clearOrganizationsCache() {
    globalCacheService.clearCache('organizations_all');
    globalCacheService.clearCachesByPrefix('sub_organizations_');
  }
  
  /**
   * Create an organization
   * @param {Object} data - Organization data
   * @returns {Promise<Object>} Created organization
   */
  static async createOrganization(data) {
    try {
      const response = await http.request(API_ENDPOINTS.organizations(), "post", data);
      // Clear organizations cache when a new organization is created
      this.clearOrganizationsCache();
      return response;
    } catch (error) {
      console.error("Error creating organization:", error);
      throw error;
    }
  }

  /**
   * Edit an organization
   * @param {string} organizationId - ID of the organization to edit
   * @param {Object} data - Updated organization data
   * @returns {Promise<Object>} Updated organization
   */
  static async editOrganization(organizationId, data) {
    try {
      const response = await http.request(
        API_ENDPOINTS.organizations(organizationId),
        "patch",
        data
      );
      // Clear organizations cache when an organization is updated
      this.clearOrganizationsCache();
      return response;
    } catch (error) {
      console.error("Error editing organization:", error);
      throw error;
    }
  }

  /**
   * Add a staff member to an organization
   * @param {string} organizationId - ID of the organization
   * @param {string} staffId - ID of the staff to add
   * @param {Object} staffData - Staff data
   * @param {boolean} autoCreateDoctorTeamForStaff - Whether to auto-create a doctor team
   * @returns {Promise<Object>} API response
   */
  static async addOrganizationStaff(
    organizationId,
    staffId,
    staffData = {},
    autoCreateDoctorTeamForStaff
  ) {
    try {
      let api = API_ENDPOINTS.organizationStaff(organizationId) + "?";
      if (staffId) {
        api += "&staffId=" + staffId;
      }
      if (staffData && staffData.dontSendInvite) {
        api += "&makeActiveInternal=true";
      }
      if (autoCreateDoctorTeamForStaff) {
        api += "&autoCreateDoctorTeamForStaff=true";
      }
      
      const response = await http.request(api, "post", staffData);
      
      // Clear organization staff cache when adding a staff member
      this.clearOrganizationStaffCache(organizationId);
      
      // Also clear global staff cache as it might be affected
      globalStaffCache.data = null;
      globalStaffCache.staffOptions = null;
      
      return response;
    } catch (error) {
      console.error("Error adding organization staff:", error);
      throw error;
    }
  }

  /**
   * Load role templates
   * @returns {Promise<Object>} Role templates data
   */
  static async loadRoleTemplates(noAllowForBulkReassigFlag) {
    const data = await http.request(API_ENDPOINTS.roleTemplates(false, noAllowForBulkReassigFlag));
    if (data && data._embedded) {
      return {
        roleTemplates: data._embedded.roleTemplates || [],
      };
    }
    return {
      roleTemplates: [],
      loadMore: false,
    };
  }

  /**
   * Create a role template
   * @param {Object} data - Role template data
   * @returns {Promise<Object>} Created role template
   */
  static async createRoleTemplate(data) {
    try {
      const response = await http.request(API_ENDPOINTS.roleTemplates(), "post", data);
      return response;
    } catch (error) {
      console.error("Error creating role template:", error);
      throw error;
    }
  }

  /**
   * Edit a role template
   * @param {string} roleId - ID of the role template to edit
   * @param {Object} data - Updated role template data
   * @returns {Promise<Object>} Updated role template
   */
  static async editRoleTemplate(roleId, data) {
    try {
      const response = await http.request(API_ENDPOINTS.roleTemplates(roleId), "patch", data);
      return response;
    } catch (error) {
      console.error("Error editing role template:", error);
      throw error;
    }
  }

  /**
   * Remove a staff member from an organization
   * @param {string} orgId - Organization ID
   * @param {string} staffId - Staff ID
   * @returns {Promise<Object>} API response
   */
  static async removeStaffFromOrganization(orgId, staffId) {
    try {
      const response = await http.request(
        API_ENDPOINTS.organizationStaff(orgId, staffId),
        "delete"
      );
      
      // Clear organization staff cache when removing a staff member
      this.clearOrganizationStaffCache(orgId);
      
      // Also clear global staff cache as it might be affected
      globalStaffCache.data = null;
      globalStaffCache.staffOptions = null;
      
      return response;
    } catch (error) {
      console.error("Error removing staff from organization:", error);
      throw error;
    }
  }

  /**
   * Load staff for a specific organization with caching
   * @param {string} organizationId - ID of the organization
   * @param {number} page - Page number for pagination
   * @returns {Promise<Object>} Staff data with pagination info
   */
  static async loadOrganizationStaff(organizationId, page) {
    // Create a cache key based on organization ID and page
    const cacheKey = `org_staff_${organizationId}_page_${page}`;
    
    try {
      // Use global cache service to prevent duplicate API calls
      return await globalCacheService.cacheApiCall(
        cacheKey,
        async () => {
          const api = API_ENDPOINTS.organizationStaff(organizationId) +
            "?page=" + page + "&size=10";
            
          const data = await http.request(api);
          
          if (data && data._embedded) {
            return {
              staff: data._embedded.staffs || [],
              pageInfo: data.page,
            };
          }
          
          return {
            staff: [],
            pageInfo: { number: -1 },
          };
        },
        5 * 60 * 1000 // 5 minute cache TTL
      );
    } catch (error) {
      console.error("Error loading organization staff:", error);
      return {
        staff: [],
        pageInfo: { number: -1 },
      };
    }
  }
  
  /**
   * Helper method to clear organization staff cache
   * @param {string} organizationId - ID of the organization
   */
  static clearOrganizationStaffCache(organizationId) {
    if (organizationId) {
      globalCacheService.clearCachesByPrefix(`org_staff_${organizationId}_`);
    } else {
      globalCacheService.clearCachesByPrefix('org_staff_');
    }
  }

  static async loadObservationHistory(patientId, observationCode, page){
    const pagination = `&page=${page}&size=30`;
    const response = await http.request(
      API_ENDPOINTS.getObservationHistory(patientId, observationCode) +
        pagination
    );
    return {
      data: response._embedded?.observationObjects || [],
      page: response.page
    };
  }

  static async getTasksCount() {
    try {
      const response = await http.request(API_ENDPOINTS.tasksCount);
      return response;
    } catch (error) {
      console.error("Error getting tasks count:", error);
      return null;
    }
  }
}

const staffService = StaffService;

export default staffService;
